import { CELL, COLS, ROWS, UNIT_LIMIT, WAVES_PER_MAP } from "../constants/board.js";
import { getEnemyDef } from "../constants/enemies.js";
import { MAPS } from "../constants/maps.js";
import { TOWER_DEFS } from "../constants/towers.js";
import { getWaveDefinition } from "../constants/waves.js";
import { dist, distSq, rand } from "../utils/math.js";
import { buildPathData } from "../utils/path.js";
import { beginAdaptiveWave, buildAdaptiveWaveContext, finalizeAdaptiveWave } from "./adaptiveDifficulty.js";

const ENEMY_BUCKET_SIZE = CELL * 2;

const nextId = (state) => state.nextId++;

const pushParticle = (state, particle) => {
	if (state.particles.length > 360) return;
	state.particles.push({
		id: nextId(state),
		...particle,
	});
};

const pushText = (state, text, x, y, color = "#ffffff") => {
	state.floatingTexts.push({
		id: nextId(state),
		text,
		x,
		y,
		vy: -0.38,
		life: 720,
		maxLife: 720,
		color,
	});
};

const notify = (state, message, tone = "info") => {
	state.notifications.push({
		id: nextId(state),
		message,
		tone,
		createdAt: Date.now(),
	});
};

const cue = (state, name, intensity = 1) => {
	state.soundCues = state.soundCues || [];
	state.soundCues.push({ id: nextId(state), name, intensity });
};

export const getPathDataForState = (state) => buildPathData(state?.mapDef || MAPS[state?.mapIdx] || MAPS[0]);

export const createSpawnQueue = (waveDef, options = {}) => {
	const queue = [];

	for (const group of waveDef) {
		for (let i = 0; i < group.count; i++) {
			queue.push({ type: group.type, delay: i * group.interval });
		}
	}

	return queue.sort((a, b) => a.delay - b.delay);
};

export const startWave = (state, pathData = getPathDataForState(state)) => {
	if (!state || state.waveActive) return false;
	if (state.runMode === "campaign" && state.wave >= WAVES_PER_MAP) return false;
	state.autoRunQueuedAt = null;

	const adaptiveContext = beginAdaptiveWave(state, pathData);
	const waveDef = getWaveDefinition({
		runMode: state.runMode,
		waveIndex: state.wave,
		mapIdx: state.mapIdx,
		mapDef: state.mapDef,
		runSeed: state.runSeed,
		adaptiveContext,
	});
	if (!waveDef) {
		if (state.adaptiveDifficulty) state.adaptiveDifficulty.inWave = null;
		return false;
	}

	const nextWave = state.wave + 1;
	state.spawnQueue = createSpawnQueue(waveDef, {
		runMode: state.runMode,
		waveNumber: nextWave,
	});
	state.spawnTimer = 0;
	state.waveActive = true;
	state.wave = nextWave;
	state.selectedTower = null;
	notify(state, `${state.runMode === "survival" ? "Survival" : "Wave"} ${state.wave} entering the lane`, "accent");
	cue(state, "wave", 1);
	return true;
};

export const spawnEnemy = (state, pathData, type) => {
	const def = getEnemyDef(type, {
		mapIdx: state.mapIdx,
		mapDef: state.mapDef,
		runMode: state.runMode,
		waveNumber: state.wave,
	});
	const start = pathData.px[0];

	return {
		id: nextId(state),
		type,
		x: start.x,
		y: start.y,
		hp: def.hp,
		maxHp: def.hp,
		speed: def.speed,
		reward: def.reward,
		color: def.color,
		size: def.size,
		heals: Boolean(def.heals),
		reinforced: Boolean(def.reinforced),
		fireman: Boolean(def.fireman),
		frostImmune: Boolean(def.frostImmune),
		pathIdx: 0,
		slowFactor: 0,
		slowEnd: 0,
		dead: false,
	};
};

export const canPlaceTower = (state, pathData, col, row) => {
	if (!state || !pathData) return { ok: false, reason: "Game is not ready." };
	if (col < 0 || col >= COLS || row < 0 || row >= ROWS) return { ok: false, reason: "Choose a grid cell." };
	if (pathData.set.has(`${col},${row}`)) return { ok: false, reason: "Towers cannot block the flight path." };
	if (state.towers.some((tower) => tower.col === col && tower.row === row)) return { ok: false, reason: "A tower already occupies that cell." };
	if (state.runMode !== "survival" && state.towers.length >= UNIT_LIMIT) return { ok: false, reason: "Unit cap reached." };
	if (!state.selectedTower) return { ok: false, reason: "Select a tower first." };

	const def = TOWER_DEFS[state.selectedTower];
	if (state.gold < def.costs[0]) return { ok: false, reason: `Need ${def.costs[0] - state.gold} more gold.` };

	return { ok: true };
};

export const placeSelectedTower = (state, pathData, col, row) => {
	const placement = canPlaceTower(state, pathData, col, row);
	if (!placement.ok) {
		notify(state, placement.reason, "warn");
		cue(state, "deny", 0.8);
		return false;
	}

	const def = TOWER_DEFS[state.selectedTower];
	state.gold -= def.costs[0];
	state.stats.goldSpent += def.costs[0];
	state.towers.push({
		id: nextId(state),
		type: state.selectedTower,
		col,
		row,
		level: 0,
		cooldown: 0,
		angle: 0,
		pulse: 420,
	});
	state.stats.towersBuilt++;
	pushText(state, `-${def.costs[0]}`, col * CELL + CELL / 2, row * CELL + CELL / 2, "#f8e45c");
	notify(state, `${def.name} online`, "success");
	cue(state, "place", 1);
	return true;
};

export const selectCell = (state, pathData, col, row) => {
	const clickedTower = state.towers.find((tower) => tower.col === col && tower.row === row) || null;

	if (state.selectedTower) {
		if (clickedTower) {
			state.selectedTower = null;
			state.selectedPlacedId = clickedTower.id;
			return;
		}
		placeSelectedTower(state, pathData, col, row);
		return;
	}

	state.selectedPlacedId = clickedTower?.id || null;
};

export const upgradeSelectedTower = (state) => {
	const tower = state.towers.find((item) => item.id === state.selectedPlacedId);
	if (!tower || tower.level >= 3) return false;

	const def = TOWER_DEFS[tower.type];
	const cost = def.costs[tower.level + 1];
	if (state.gold < cost) {
		notify(state, `Upgrade needs ${cost - state.gold} more gold`, "warn");
		return false;
	}

	state.gold -= cost;
	state.stats.goldSpent += cost;
	tower.level++;
	state.stats.towersUpgraded++;
	tower.pulse = 620;
	pushText(state, "UPGRADE", tower.col * CELL + CELL / 2, tower.row * CELL + CELL / 2, def.color);
	notify(state, `${def.name} upgraded to Lv.${tower.level + 1}`, "success");
	cue(state, "upgrade", 1);
	return true;
};

export const sellSelectedTower = (state) => {
	const index = state.towers.findIndex((item) => item.id === state.selectedPlacedId);
	if (index === -1) return false;

	const tower = state.towers[index];
	const def = TOWER_DEFS[tower.type];
	let invested = def.costs[0];
	for (let i = 1; i <= tower.level; i++) invested += def.costs[i];

	const refund = Math.floor(invested * 0.6);
	state.gold += refund;
	state.stats.towersSold++;
	state.towers.splice(index, 1);
	state.selectedPlacedId = null;
	notify(state, `${def.name} sold for ${refund} gold`, "info");
	cue(state, "sell", 0.8);
	return true;
};

const canDamageEnemy = (enemy, projectile, now) => {
	if (enemy.reinforced) return projectile.sourceType === "sniper" && projectile.sourceLevel >= 1;
	if (enemy.fireman) return projectile.sourceType === "frost" || now < enemy.slowEnd;
	return true;
};

const damageEnemy = (state, enemy, projectile, amount, now) => {
	if (!canDamageEnemy(enemy, projectile, now)) {
		pushText(state, "ARMOR", enemy.x, enemy.y - enemy.size, "#c8d2dc");
		cue(state, "armor", 0.45);
		return false;
	}

	enemy.hp -= amount;
	state.stats.damage += amount;
	cue(state, projectile.sourceType === "tesla" ? "teslaHit" : projectile.sourceType === "frost" ? "frostHit" : "hit", 0.28);

	for (let i = 0; i < 4; i++) {
		pushParticle(state, {
			x: enemy.x,
			y: enemy.y,
			vx: rand(-2.3, 2.3),
			vy: rand(-2.3, 2.3),
			life: 280,
			maxLife: 280,
			color: projectile.color,
			size: rand(1.8, 3.8),
		});
	}

	if (projectile.slow && !enemy.frostImmune) {
		enemy.slowFactor = Math.max(enemy.slowFactor || 0, projectile.slow);
		enemy.slowEnd = now + projectile.slowDur;
		pushText(state, "SLOW", enemy.x, enemy.y - enemy.size, "#7cf7d4");
		cue(state, "slow", 0.34);
	}

	if (enemy.hp <= 0) {
		enemy.dead = true;
		const multiplier = getActiveScoreMultiplier(state, now);
		const goldGain = Math.round(enemy.reward * multiplier);
		const scoreGain = Math.round(enemy.reward * 2 * multiplier);
		state.gold += goldGain;
		state.score += scoreGain;
		state.stats.kills++;
		if (enemy.type === "boss") state.stats.bossesKilled++;
		cue(state, enemy.type === "boss" ? "bossKill" : "kill", enemy.type === "boss" ? 1 : 0.55);

		pushText(state, `+${goldGain}g`, enemy.x, enemy.y, "#f8e45c");
		for (let i = 0; i < 12; i++) {
			pushParticle(state, {
				x: enemy.x,
				y: enemy.y,
				vx: rand(-4.2, 4.2),
				vy: rand(-4.2, 4.2),
				life: 520,
				maxLife: 520,
				color: enemy.color,
				size: rand(2.8, 6),
			});
		}
	}

	return true;
};

const applyDamage = (state, target, projectile, now) => {
	const primaryHit = damageEnemy(state, target, projectile, projectile.damage, now);
	if (!primaryHit) return;

	if (projectile.splash) {
		for (const enemy of state.enemies) {
			if (enemy.dead || enemy === target) continue;
			if (dist(target, enemy) <= projectile.splash) {
				damageEnemy(state, enemy, projectile, projectile.damage * 0.5, now);
			}
		}
		state.beams.push({
			id: nextId(state),
			x1: target.x,
			y1: target.y,
			x2: target.x,
			y2: target.y,
			radius: projectile.splash,
			color: projectile.color,
			life: 220,
			maxLife: 220,
			kind: "ring",
		});
	}

	if (projectile.chains) {
		let last = target;
		let chainsLeft = projectile.chains;
		const hit = new Set([target.id]);

		while (chainsLeft > 0) {
			let closest = null;
			let closestD = 80;
			for (const enemy of state.enemies) {
				if (enemy.dead || hit.has(enemy.id)) continue;
				const d = dist(last, enemy);
				if (d < closestD) {
					closestD = d;
					closest = enemy;
				}
			}
			if (!closest) break;

			hit.add(closest.id);
			state.beams.push({
				id: nextId(state),
				x1: last.x,
				y1: last.y,
				x2: closest.x,
				y2: closest.y,
				color: projectile.color,
				life: 180,
				maxLife: 180,
				kind: "arc",
			});
			damageEnemy(state, closest, projectile, projectile.damage * 0.6, now);
			last = closest;
			chainsLeft--;
		}
	}
};

export const getActiveScoreMultiplier = (state, now = Date.now()) => {
	const boost = state.adRewards?.activeScoreMultiplier;
	if (!boost || now > boost.expiresAt) return 1;
	return boost.multiplier || 1;
};

const getEnemyBucketKey = (x, y) => `${Math.floor(x / ENEMY_BUCKET_SIZE)},${Math.floor(y / ENEMY_BUCKET_SIZE)}`;

const buildEnemyBuckets = (enemies) => {
	const buckets = new Map();
	for (const enemy of enemies) {
		const key = getEnemyBucketKey(enemy.x, enemy.y);
		if (!buckets.has(key)) buckets.set(key, []);
		buckets.get(key).push(enemy);
	}
	return buckets;
};

const getNearbyEnemies = (buckets, point, radius) => {
	const radiusSq = radius * radius;
	const bucketRadius = Math.ceil(radius / ENEMY_BUCKET_SIZE);
	const centerX = Math.floor(point.x / ENEMY_BUCKET_SIZE);
	const centerY = Math.floor(point.y / ENEMY_BUCKET_SIZE);
	const nearby = [];

	for (let offsetY = -bucketRadius; offsetY <= bucketRadius; offsetY += 1) {
		for (let offsetX = -bucketRadius; offsetX <= bucketRadius; offsetX += 1) {
			const bucket = buckets.get(`${centerX + offsetX},${centerY + offsetY}`);
			if (!bucket) continue;
			for (const enemy of bucket) {
				if (distSq(point, enemy) <= radiusSq) nearby.push(enemy);
			}
		}
	}

	return nearby;
};

export const tickGame = (state, pathData, dt, settings = {}) => {
	if (!state || !pathData || state.paused || state.lives <= 0) return;

	const speedDt = dt * state.speed;
	const now = performance.now();
	const pathPx = pathData.px;
	const effectsScaleBase = settings.performanceMode ? 0.4 : settings.effectsIntensity === "low" ? 0.65 : settings.effectsIntensity === "high" ? 1.25 : 1;

	if (state.spawnQueue.length > 0) {
		state.spawnTimer += speedDt;
		while (state.spawnQueue.length > 0 && state.spawnTimer >= state.spawnQueue[0].delay) {
			state.enemies.push(spawnEnemy(state, pathData, state.spawnQueue.shift().type));
		}
	} else if (state.waveActive && state.enemies.length === 0) {
		state.waveActive = false;
		state.autoRunQueuedAt = null;
		finalizeAdaptiveWave(state);
		state.stats.wavesCleared++;
		if (state.runMode === "survival") state.stats.survivalWavesCleared++;
		state.adRewards = state.adRewards || {};
		state.adRewards.availableBetweenWaveCacheWave = state.wave;
		const bonus = 20 + state.wave * 5;
		state.gold += bonus;
		notify(state, `Wave ${state.wave} cleared. +${bonus} gold`, "success");
		cue(state, "waveClear", 1);
		if ((state.autoRun || false) && (state.runMode !== "campaign" || state.wave < WAVES_PER_MAP)) {
			state.autoRunQueuedAt = Date.now() + 700;
		}
	}

	if (!state.waveActive && state.autoRun && state.autoRunQueuedAt && Date.now() >= state.autoRunQueuedAt) {
		startWave(state, pathData);
	}

	for (const enemy of state.enemies) {
		if (enemy.dead) continue;
		const slow = now < enemy.slowEnd ? 1 - enemy.slowFactor : 1;
		let moved = enemy.speed * slow * (speedDt / 16);

		while (moved > 0 && enemy.pathIdx < pathPx.length - 1) {
			const target = pathPx[enemy.pathIdx + 1];
			const d = dist(enemy, target);
			if (d <= moved) {
				enemy.x = target.x;
				enemy.y = target.y;
				moved -= d;
				enemy.pathIdx++;
			} else {
				const ratio = moved / d;
				enemy.x += (target.x - enemy.x) * ratio;
				enemy.y += (target.y - enemy.y) * ratio;
				moved = 0;
			}
		}

		if (enemy.pathIdx >= pathPx.length - 1) {
			enemy.dead = true;
			state.lives--;
			state.stats.leaks++;
			notify(state, "A hostile breached the colony shield", "danger");
			cue(state, "leak", 1);
		}
	}

	const aliveEnemies = state.enemies.filter((enemy) => !enemy.dead);
	const enemyById = new Map(aliveEnemies.map((enemy) => [enemy.id, enemy]));
	const enemyBuckets = buildEnemyBuckets(aliveEnemies);
	const densityPenalty = aliveEnemies.length > 120 ? 0.35 : aliveEnemies.length > 80 ? 0.55 : aliveEnemies.length > 50 ? 0.75 : 1;
	const effectsScale = effectsScaleBase * densityPenalty;

	for (const enemy of aliveEnemies) {
		if (!enemy.heals) continue;
		for (const other of getNearbyEnemies(enemyBuckets, enemy, 60)) {
			if (other === enemy) continue;
			other.hp = Math.min(other.maxHp, other.hp + 0.15 * (speedDt / 16));
		}
	}

	for (const tower of state.towers) {
		tower.cooldown = (tower.cooldown || 0) - speedDt;
		tower.pulse = Math.max(0, (tower.pulse || 0) - speedDt);
		if (tower.cooldown > 0) continue;

		const def = TOWER_DEFS[tower.type];
		const level = tower.level;
		const range = def.range[level];
		const towerPos = { x: tower.col * CELL + CELL / 2, y: tower.row * CELL + CELL / 2 };
		let target = null;
		let bestProgress = -1;

		for (const enemy of getNearbyEnemies(enemyBuckets, towerPos, range)) {
			if (enemy.pathIdx > bestProgress) {
				bestProgress = enemy.pathIdx;
				target = enemy;
			}
		}

		if (!target) continue;

		tower.cooldown = def.rate[level];
		tower.angle = Math.atan2(target.y - towerPos.y, target.x - towerPos.x);
		cue(state, tower.type === "tesla" ? "teslaShot" : tower.type === "frost" ? "frostShot" : "shot", tower.type === "sniper" ? 0.45 : 0.22);
		state.projectiles.push({
			id: nextId(state),
			x: towerPos.x,
			y: towerPos.y,
			targetId: target.id,
			speed: tower.type === "sniper" ? 9 : 6,
			damage: def.damage[level],
			color: def.color,
			splash: def.splash ? def.splash[level] : 0,
			slow: def.slow ? def.slow[level] : 0,
			slowDur: def.slowDur ? def.slowDur[level] : 0,
			chains: def.chains ? def.chains[level] : 0,
			sourceType: tower.type,
			sourceLevel: level,
		});

		if (Math.random() < 0.35 * effectsScale) {
			pushParticle(state, {
				x: towerPos.x,
				y: towerPos.y,
				vx: Math.cos(tower.angle) * 1.8,
				vy: Math.sin(tower.angle) * 1.8,
				life: 160,
				maxLife: 160,
				color: def.color,
				size: 2.2,
			});
		}
	}

	for (const projectile of state.projectiles) {
		if (projectile.dead) continue;
		const target = enemyById.get(projectile.targetId);
		if (!target) {
			projectile.dead = true;
			continue;
		}

		const d = dist(projectile, target);
		const step = projectile.speed * (speedDt / 16);
		if (d <= step + target.size) {
			projectile.dead = true;
			applyDamage(state, target, projectile, now);
		} else {
			const ratio = step / d;
			projectile.x += (target.x - projectile.x) * ratio;
			projectile.y += (target.y - projectile.y) * ratio;
		}
	}

	for (const particle of state.particles) {
		particle.life -= speedDt;
		particle.x += particle.vx * (speedDt / 16);
		particle.y += particle.vy * (speedDt / 16);
		particle.vx *= 0.96;
		particle.vy *= 0.96;
	}

	for (const beam of state.beams) beam.life -= speedDt;
	for (const text of state.floatingTexts) {
		text.life -= speedDt;
		text.y += text.vy * (speedDt / 16);
	}

	state.enemies = aliveEnemies.filter((enemy) => !enemy.dead);
	state.projectiles = state.projectiles.filter((projectile) => !projectile.dead);
	state.particles = state.particles.filter((particle) => particle.life > 0);
	state.beams = state.beams.filter((beam) => beam.life > 0);
	state.floatingTexts = state.floatingTexts.filter((text) => text.life > 0);

	if (state.adRewards?.activeScoreMultiplier && Date.now() > state.adRewards.activeScoreMultiplier.expiresAt) {
		state.adRewards.activeScoreMultiplier = null;
	}

	if (state.runMode === "campaign" && state.wave >= WAVES_PER_MAP && !state.waveActive && state.enemies.length === 0 && state.spawnQueue.length === 0) {
		state.selectedTower = null;
	}
};

export const getScreenForState = (state) => {
	if (!state) return "menu";
	if (state.lives <= 0) return "gameover";
	if (state.runMode === "survival") return "playing";
	if (state.wave >= WAVES_PER_MAP && !state.waveActive && state.enemies.length === 0 && state.spawnQueue.length === 0) {
		return state.mapIdx >= MAPS.length - 1 ? "victory" : "mapComplete";
	}
	return "playing";
};

export const getAdaptivePreviewContext = (state, pathData = getPathDataForState(state)) => buildAdaptiveWaveContext(state, pathData);

export const toggleAutoRun = (state) => {
	if (!state) return false;
	state.autoRun = !state.autoRun;
	if (!state.autoRun) {
		state.autoRunQueuedAt = null;
		return false;
	}
	if (!state.waveActive && (state.runMode !== "campaign" || state.wave < WAVES_PER_MAP)) {
		state.autoRunQueuedAt = Date.now() + 350;
	}
	return true;
};
